import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

interface Props {
  orgSlug: string;
  projectSlug: string;
  rowId: string;
  verifyStartedAt: string | null;
}

const WINDOW_MS = 5 * 60_000;

/**
 * Step 3 — verifying spinner with elapsed time. After 5 minutes since
 * verify_started_at, swaps to a manual "Re-verify" button. The parent's
 * polling query (refetchInterval) drives the actual data refresh.
 */
export function VerifyingStep({ orgSlug, projectSlug, rowId, verifyStartedAt }: Props) {
  const qc = useQueryClient();
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, []);

  const startedAt = verifyStartedAt ? new Date(verifyStartedAt).getTime() : now;
  const elapsedMs = Math.max(0, now - startedAt);
  const elapsedLabel = formatElapsed(elapsedMs);
  const timedOut = elapsedMs > WINDOW_MS;

  const retry = useMutation({
    mutationFn: () =>
      api.post(`/orgs/${orgSlug}/projects/${projectSlug}/domains/${rowId}/verify/manual-retry`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["project-domains", orgSlug, projectSlug] }),
  });

  if (timedOut) {
    return (
      <div className="space-y-3">
        <div className="text-xs">
          We've been checking for over 5 minutes. DNS hasn't propagated yet (or the record may be wrong).
        </div>
        <div className="flex justify-end">
          <Button
            variant="primary"
            size="sm"
            disabled={retry.isPending}
            onClick={() => retry.mutate()}
          >
            {retry.isPending && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
            Re-verify
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2 text-xs">
        <Loader2 className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
        <span>Checking DNS — elapsed {elapsedLabel}</span>
      </div>
      <div className="text-2xs text-subtle-foreground">
        Polling Cloudflare + Google every 5 seconds. We'll advance automatically.
      </div>
    </div>
  );
}

function formatElapsed(ms: number): string {
  const s = Math.floor(ms / 1000);
  if (s < 60) return `${s}s`;
  return `${Math.floor(s / 60)}m ${s % 60}s`;
}
